import { isErpApiSuccess } from "../erpClient";

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const isPresent = (v) => v !== undefined && v !== null && v !== "";

/** First finite number from candidates (ignores empty strings). */
const pickNum = (...vals) => {
  for (const v of vals) {
    if (!isPresent(v)) continue;
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

function extractOldMetalListRows(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.purchases)) return d.purchases;
  if (Array.isArray(d.rows)) return d.rows;
  if (Array.isArray(d.purchase_details)) return d.purchase_details;
  if (Array.isArray(d.data)) return d.data;
  // Single-document show shape only (not paginated list wrapper)
  if (d.id != null || d.doc_number || d.purchase_no) return [d];
  return [];
}

function unwrapOldMetalListRow(item) {
  const pd = item?.purchase_details;
  if (pd && typeof pd === "object" && !Array.isArray(pd)) {
    return { ...pd, ...item };
  }
  return item;
}

function getOldMetalLineItems(item) {
  for (const key of ["items", "purchase_items"]) {
    const arr = item[key];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
}

function sumItemField(items, ...keys) {
  return items.reduce((sum, item) => {
    const v = pickNum(...keys.map((k) => item[k]));
    return sum + (v ?? 0);
  }, 0);
}

function pickOldMetalWeight(item, topKeys, itemKeys, lineItems) {
  const fromItems = lineItems.length
    ? sumItemField(lineItems, ...itemKeys)
    : 0;
  if (fromItems > 0) return fromItems;

  const fromTop = pickNum(...topKeys.map((k) => item[k]));
  if (fromTop !== null) return fromTop;

  const summary = item.summary || item.totals;
  if (summary && typeof summary === "object") {
    const fromSummary = pickNum(...topKeys.map((k) => summary[k]));
    if (fromSummary !== null) return fromSummary;
  }

  return fromItems;
}

export const isOldMetalApiSuccess = (res) =>
  isErpApiSuccess(res) ||
  (res &&
    (res.success === true ||
      String(res.status || "").toLowerCase() === "success" ||
      res.message === "radha"));

export function createOldMetalIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Extract single document from show_old_metal_purchase response. */
export function pickOldMetalShowDocument(res) {
  if (!isOldMetalApiSuccess(res)) return null;
  const d = res?.data;
  if (Array.isArray(d) && d.length) return d[0];
  if (d && typeof d === "object") return d;
  return null;
}

/**
 * Normalize list rows for MetalPurchaseTable (legacy column names).
 */
export function normalizeOldMetalListRows(res) {
  if (!isOldMetalApiSuccess(res)) return [];

  return extractOldMetalListRows(res).map((raw) => {
    const item = unwrapOldMetalListRow(raw);
    const lineItems = getOldMetalLineItems(item);

    const totalGwt = pickOldMetalWeight(
      item,
      [
        "total_gwt",
        "totalGwt",
        "total_gross_wt",
        "totalGrossWt",
        "gwt",
        "gross_weight",
      ],
      ["gwt", "gross_weight", "grossWeight"],
      lineItems,
    );
    const totalNwt = pickOldMetalWeight(
      item,
      [
        "total_nwt",
        "totalNwt",
        "total_net_wt",
        "totalNetWt",
        "nwt",
        "net_weight",
      ],
      ["nwt", "net_weight", "netWeight"],
      lineItems,
    );
    const totalFine = pickOldMetalWeight(
      item,
      [
        "total_fine",
        "total_fine_wt",
        "totalFineWt",
        "totalFine",
        "total_finewt",
        "total_fwt",
        "fine_wt",
        "fine_weight",
        "gold_gm",
        "gold_weight",
      ],
      ["fine_wt", "fine_weight", "fineWeight", "fine"],
      lineItems,
    );

    return {
      id: item.id ?? "",
      purchase_no: item.doc_number || item.purchase_no || "",
      date: item.doc_date || item.date || item.bill_date || "",
      user_id: item.customer_id ?? item.user_id ?? "",
      user_name: item.customer_name || item.user_name || "",
      user_mobile: item.customer_mobile || item.user_mobile || "",
      total_gwt: num(totalGwt, 0),
      total_nwt: num(totalNwt, 0),
      total_fine: num(totalFine, 0),
      total_amt: num(
        item.grand_total ?? item.total_amt ?? item.total_amount,
        0,
      ),
    };
  });
}

/**
 * List endpoint may omit weight totals; fill from show() when amount exists but weights are zero.
 */
export async function enrichOldMetalListWeights(rows, showById) {
  if (!Array.isArray(rows) || !rows.length || typeof showById !== "function") {
    return rows;
  }

  const needsEnrichment = rows.filter(
    (p) =>
      p.id &&
      Number(p.total_amt) > 0 &&
      Number(p.total_gwt) === 0 &&
      Number(p.total_nwt) === 0 &&
      Number(p.total_fine) === 0,
  );
  if (!needsEnrichment.length) return rows;

  const byId = new Map(rows.map((r) => [String(r.id), r]));

  await Promise.all(
    needsEnrichment.map(async (p) => {
      try {
        const response = await showById({ id: p.id });
        const doc = pickOldMetalShowDocument(response);
        const cached = mapOldMetalShowToLegacyCached(doc);
        const details = cached?.purchase_details;
        if (!details) return;

        byId.set(String(p.id), {
          ...p,
          total_gwt: num(details.total_gwt, 0),
          total_nwt: num(details.total_nwt, 0),
          total_fine: num(details.total_fine, 0),
        });
      } catch {
        // keep original row
      }
    }),
  );

  return rows.map((r) => byId.get(String(r.id)) ?? r);
}

/**
 * Map newERP document → shape expected by MetalPurchaseTable PDF cache + EditOldPurchase loader.
 * Top-level: { purchase_details, purchase_items, payments }
 */
export function mapOldMetalShowToLegacyCached(doc) {
  if (!doc) return null;
  const items = getOldMetalLineItems(doc);
  const payments = Array.isArray(doc.payments) ? doc.payments : [];

  const purchaseItems = items.map((it, idx) => ({
    id: it.id ?? idx + 1,
    product_group_id: it.product_group_id ?? it.product_group ?? "",
    product_group_name:
      it.product_group_name ?? it.product_group_label ?? "",
    item_name: it.tag_no || it.item_name || "",
    pcs: it.pcs ?? 0,
    ct: it.ct ?? 0,
    gwt: pickNum(it.gwt, it.gross_weight, it.grossWeight) ?? 0,
    lwt: it.lwt ?? 0,
    nwt: pickNum(it.nwt, it.net_weight, it.netWeight) ?? 0,
    touch: it.purity ?? it.touch ?? 0,
    wastage: it.wastage ?? 0,
    fine_wt: pickNum(it.fine_wt, it.fine_weight, it.fineWeight, it.fine) ?? 0,
    total_amt: it.total_amount ?? it.total_amt ?? 0,
    rate: it.rate,
    image: it.image ?? null,
  }));

  const totalGwt = pickOldMetalWeight(
    doc,
    ["total_gwt", "totalGwt", "total_gross_wt", "gwt", "gross_weight"],
    ["gwt", "gross_weight", "grossWeight"],
    items,
  );
  const totalNwt = pickOldMetalWeight(
    doc,
    ["total_nwt", "totalNwt", "total_net_wt", "nwt", "net_weight"],
    ["nwt", "net_weight", "netWeight"],
    items,
  );
  const totalFine = pickOldMetalWeight(
    doc,
    [
      "total_fine",
      "total_fine_wt",
      "totalFineWt",
      "fine_wt",
      "fine_weight",
      "gold_gm",
    ],
    ["fine_wt", "fine_weight", "fineWeight", "fine"],
    items,
  );
  const grandTotal = num(doc.grand_total, 0);

  const mappedPayments = payments.map((p) => ({
    id: p.id,
    transaction_id: p.transaction_id,
    idempotency_key: p.idempotency_key,
    transaction_date: p.transaction_date,
    amount: p.amount,
    payment_mode: p.payment_mode,
    bank_name: p.bank_name,
    bank_account_id: p.bank_account_id,
    bank_register_id: p.bank_register_id,
    remarks: p.remarks,
  }));

  return {
    purchase_details: {
      user_id: doc.customer_id ?? doc.user_id,
      user_name: doc.customer_name ?? doc.user_name ?? "",
      user_mobile: doc.customer_mobile ?? doc.user_mobile ?? "",
      address: doc.customer_address ?? doc.address ?? "",
      area: doc.area ?? "",
      city: doc.city ?? "",
      state: doc.state ?? "",
      date: doc.doc_date || doc.date || "",
      bill_date: doc.doc_date || doc.bill_date || doc.date || "",
      purchase_no: doc.doc_number || doc.purchase_no || "",
      grand_total: grandTotal,
      total_gwt: totalGwt,
      total_nwt: totalNwt,
      total_fine: totalFine,
      total_amt: grandTotal,
      gst_percentage: doc.gst_percentage ?? doc.gst_percent ?? 0,
      gst_amount: num(doc.tax_amount ?? doc.gst_amount, 0),
      round_off: num(doc.round_off, 0),
      note: doc.notes ?? doc.note ?? "",
      bill_type: doc.bill_type,
      bill_type_name: doc.bill_type_name,
      bill_type_applicable: doc.bill_type_applicable,
      bill_type_gst: doc.bill_type_gst,
    },
    purchase_items: purchaseItems,
    payments: mappedPayments,
  };
}
